import styles from './FormComprador.module.scss';
import React, { useState } from 'react';
import { IComprador } from '../../../interfaces/comprador.interface';
import useCreateComprador from '../../../hooks/compradores/useCreateComprador';
import { validarCNPJ, validarEmail, validarTelefone } from '../../../utils/validações';

const estadosBrasil = [
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' }
];

const FormularioComprador: React.FC = () => {
    const { create, loading, error } = useCreateComprador();
    const [comprador, setComprador] = useState<IComprador>({
        razaoSocial: '',
        cnpj: '',
        inscricaoMunicipal: '',
        inscricaoEstadual: '',
        endereco: '',
        telefone: '',
        celular: '',
        email: '',
        cep: '',
        municipio: '',
        uf: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateField = (name: string, value: string) => {
        let message = '';
        switch (name) {
            case 'cnpj':
                if (value && !validarCNPJ(value)) message = 'CNPJ inválido';
                break;
            case 'email':
                if (value && !validarEmail(value)) message = 'E-mail inválido';
                break;
            case 'telefone':
            case 'celular':
                if (value && !validarTelefone(value)) {
                    message = `${name === 'telefone' ? 'Telefone' : 'Celular'} inválido`;
                }
                break;
            default:
                break;
        }
        return message;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setComprador({
            ...comprador,
            [name]: value
        });

        const errorMessage = validateField(name, value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: errorMessage
        }));
    };

    const validarFormulario = () => {
        let valid = true;
        const novosErros: { [key: string]: string } = {};

        Object.keys(comprador).forEach((key) => {
            const error = validateField(key, comprador[key as keyof IComprador] as string);
            if (error) {
                novosErros[key] = error;
                valid = false;
            }
        });

        setErrors(novosErros);
        return valid;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validarFormulario()) {
            return;
        }
        try {
            await create(comprador);
            console.log(comprador);
            setComprador({
                razaoSocial: '',
                cnpj: '',
                inscricaoMunicipal: '',
                inscricaoEstadual: '',
                endereco: '',
                telefone: '',
                celular: '',
                email: '',
                cep: '',
                municipio: '',
                uf: ''
            }); // Limpa o formulário após criar
            setErrors({}); // Limpa os erros após o sucesso
        } catch (err) {
            console.error(error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <div>
                <div>
                    <label>Razão Social:</label>
                    <input
                        type="text"
                        name="razaoSocial"
                        value={comprador.razaoSocial}
                        onChange={handleChange}
                    />
                    {errors.razaoSocial && <span className="error">{errors.razaoSocial}</span>}
                </div>

                <div>
                    <label>CNPJ:</label>
                    <input
                        type="text"
                        name="cnpj"
                        value={comprador.cnpj}
                        onChange={handleChange}
                    />
                    {errors.cnpj && <span className="error">{errors.cnpj}</span>}
                </div>

                <div>
                    <label>Inscrição Municipal:</label>
                    <input
                        type="text"
                        name="inscricaoMunicipal"
                        value={comprador.inscricaoMunicipal}
                        onChange={handleChange}
                    />
                </div>

                <div>
                    <label>Inscrição Estadual:</label>
                    <input
                        type="text"
                        name="inscricaoEstadual"
                        value={comprador.inscricaoEstadual}
                        onChange={handleChange}
                    />
                </div>

                <div>
                    <label>Telefone:</label>
                    <input
                        type="text"
                        name="telefone"
                        value={comprador.telefone}
                        onChange={handleChange}
                    />
                    {errors.telefone && <span className="error">{errors.telefone}</span>}
                </div>

                <div>
                    <label>Celular:</label>
                    <input
                        type="text"
                        name="celular"
                        value={comprador.celular}
                        onChange={handleChange}
                    />
                    {errors.celular && <span className="error">{errors.celular}</span>}
                </div>

                <div>
                    <label>Email:</label>
                    <input
                        type="email"
                        name="email"
                        value={comprador.email}
                        onChange={handleChange}
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </div>
            </div>

            <div>
                <div>
                    <label>Endereço:</label>
                    <input
                        type="text"
                        name="endereco"
                        value={comprador.endereco}
                        onChange={handleChange}
                    />
                    {errors.endereco && <span className="error">{errors.endereco}</span>}
                </div>

                <div>
                    <label>CEP:</label>
                    <input
                        type="text"
                        name="cep"
                        value={comprador.cep}
                        onChange={handleChange}
                    />
                    {errors.cep && <span className="error">{errors.cep}</span>}
                </div>

                <div>
                    <label>Município:</label>
                    <input
                        type="text"
                        name="municipio"
                        value={comprador.municipio}
                        onChange={handleChange}
                    />
                    {errors.municipio && <span className="error">{errors.municipio}</span>}
                </div>

                <div>
                    <label>UF:</label>
                    <select
                        name="uf"
                        value={comprador.uf}
                        onChange={handleChange}
                    >
                        <option value="">Selecione</option>
                        {estadosBrasil.map((estado) => (
                            <option key={estado.sigla} value={estado.sigla}>
                                {estado.nome}
                            </option>
                        ))}
                    </select>
                    {errors.uf && <span className="error">{errors.uf}</span>}
                </div>

                <button type="submit" disabled={loading}>
                    {loading ? 'Cadastrando...' : 'Cadastrar Comprador'}
                </button>

                {error && <p className="error">Erro ao cadastrar: {error}</p>}
            </div>
        </form>
    );
};

export default FormularioComprador;
